import React, { useCallback, useEffect, useState } from 'react';
import { BookOpen, Download, FileText, Image as ImageIcon } from 'lucide-react';
import {
  knowledgeExportService,
  KnowledgeExportSummary,
} from '../../services/knowledgeExportService';
import { Button, Card, Checkbox, SectionHeader, Skeleton, StatGrid } from '../ui';

const formatBytes = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  const value = bytes / 1024 ** i;
  return `${i === 0 ? value : value.toFixed(value >= 10 ? 0 : 1).replace(/\.0$/, '')} ${units[i]}`;
};

const today = () => new Date().toISOString().slice(0, 10);

export const KnowledgeExport: React.FC = () => {
  const [summary, setSummary] = useState<KnowledgeExportSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [includeMedia, setIncludeMedia] = useState(true);
  const [includeData, setIncludeData] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    knowledgeExportService
      .getSummary()
      .then((data) => {
        if (!cancelled) setSummary(data);
      })
      .catch(() => {
        if (!cancelled) setError("Couldn't read what's available to export.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleDownload = useCallback(async () => {
    setDownloading(true);
    setError(null);
    try {
      const blob = await knowledgeExportService.download({ includeMedia, includeData });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `staketory-knowledge-base-${today()}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setError("The export didn't finish. Try again, or exclude media files if it timed out.");
    } finally {
      setDownloading(false);
    }
  }, [includeMedia, includeData]);

  const size = summary
    ? includeMedia
      ? summary.estimatedBytes.withMedia
      : summary.estimatedBytes.withoutMedia
    : 0;

  return (
    <section>
      <SectionHeader
        title="Knowledge base export"
        aside={summary ? <span>about {formatBytes(size)}</span> : undefined}
      />

      {loading ? (
        <Skeleton className="h-24 w-full rounded-2xl" />
      ) : (
        <>
          {summary && (
            <StatGrid
              className="mb-3"
              items={[
                { label: 'People', value: summary.counts.stakeholders.toLocaleString() },
                { label: 'Organizations', value: summary.counts.organizations.toLocaleString() },
                { label: 'Wiki pages', value: summary.counts.wikiPages.toLocaleString() },
                {
                  label: 'Files',
                  value: summary.counts.mediaFiles.toLocaleString(),
                  hint: formatBytes(summary.media.bytes),
                },
              ]}
            />
          )}

          <Card padded>
            <p className="text-[13px] text-neutral-600">
              One zip holding the whole directory as linked Markdown pages, plus an{' '}
              <span className="text-ink">index.html</span> that opens in any browser with no
              internet. Nothing to install, nothing to log into.
            </p>

            <ul className="mt-3 space-y-1.5 text-[12px] text-neutral-500">
              <li className="flex items-center gap-2">
                <FileText className="h-3.5 w-3.5 text-neutral-400" strokeWidth={1.75} />
                A page per person and organization, linked to each other
              </li>
              <li className="flex items-center gap-2">
                <BookOpen className="h-3.5 w-3.5 text-neutral-400" strokeWidth={1.75} />
                Wiki pages, relationships, roles and influence scores
              </li>
              <li className="flex items-center gap-2">
                <ImageIcon className="h-3.5 w-3.5 text-neutral-400" strokeWidth={1.75} />
                Photos and documents, shown inline
              </li>
            </ul>

            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2.5">
              <Checkbox
                id="export-media"
                label="Include photos and documents"
                checked={includeMedia}
                onChange={(e) => setIncludeMedia(e.target.checked)}
              />
              <Checkbox
                id="export-data"
                label="Include raw JSON"
                checked={includeData}
                onChange={(e) => setIncludeData(e.target.checked)}
              />
            </div>

            {error && <p className="mt-3 text-[12px] text-primary-600">{error}</p>}

            <div className="mt-4 flex items-center gap-3">
              <Button onClick={handleDownload} disabled={downloading}>
                <Download className="h-4 w-4" strokeWidth={1.75} />
                {downloading ? 'Preparing…' : 'Download export'}
              </Button>
              {downloading && (
                <span className="text-[12px] text-neutral-500">
                  Building the archive — this can take a few minutes with media.
                </span>
              )}
            </div>
          </Card>
        </>
      )}
    </section>
  );
};
